import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';

import {
  customFieldsError,
  fieldInputError,
  toFormValue,
  toPatch,
  type CustomFieldItem,
} from '../presentation';
import { useFillCustomFields } from '../use-custom-fields';
import { CustomFieldControl } from './CustomFieldControl';

// Edición de los datos adicionales de un documento (QA F1 #14): un control por campo
// según su tipo, validación antes de enviar, solo viaja lo que cambió y cada rechazo
// del API queda en su campo, conservando lo escrito (§1.5, UX-ERR-01).

export function CustomFieldsModal({
  open,
  entryId,
  code,
  fields,
  onClose,
}: {
  open: boolean;
  entryId: string;
  code: string;
  fields: CustomFieldItem[];
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const fill = useFillCustomFields(entryId);
  const { showToast } = useToast();
  // Solo lo que la persona tocó; lo demás se muestra con el valor vigente del servidor.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Los campos tal como estaban al abrir: el cambio se calcula contra esto, no contra un
  // refetch, para no devolver al servidor el cambio de otro usuario (adversarial de cierre).
  const [opened, setOpened] = useState<Record<string, CustomFieldItem>>({});
  const [checked, setChecked] = useState(false);
  const [server, setServer] = useState<ReturnType<typeof customFieldsError> | null>(null);

  // Se reinicia solo al ABRIR: un refetch de la ficha con el diálogo abierto (p. ej. la
  // invalidación tras un rechazo) trae `fields` con otra referencia y no debe borrar lo
  // escrito ni el error del servidor (Lente B del lote).
  const [shownOpen, setShownOpen] = useState(false);
  if (open !== shownOpen) {
    setShownOpen(open);
    if (open) {
      setEdits({});
      setOpened(Object.fromEntries(fields.map((f) => [f.key, f])));
      setChecked(false);
      setServer(null);
    }
  }

  const valueOf = (field: CustomFieldItem): string => edits[field.key] ?? toFormValue(field);
  const errorOf = (field: CustomFieldItem): string | null =>
    server?.fields[field.key] ?? (checked ? fieldInputError(field, valueOf(field)) : null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    const invalid = fields.some((f) => fieldInputError(f, valueOf(f)) !== null);
    if (invalid || fill.isPending) {
      return;
    }
    const patch = toPatch(
      fields.map((f) => opened[f.key] ?? f),
      edits,
    );
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await fill.mutateAsync(patch);
      showToast(`Datos adicionales de ${code} actualizados`);
      onClose();
    } catch (err) {
      setServer(customFieldsError(err));
    }
  }

  return (
    <ModalShell
      open={open}
      title="Editar datos adicionales"
      wide
      busy={fill.isPending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={fill.isPending}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={fill.isPending}>
            {fill.isPending ? 'Guardando…' : 'Guardar datos'}
          </button>
        </>
      }
    >
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        {fields.map((field) => (
          <CustomFieldControl
            key={field.key}
            field={field}
            value={valueOf(field)}
            error={errorOf(field)}
            onChange={(value) => {
              setEdits((f) => ({ ...f, [field.key]: value }));
              setServer(null);
            }}
          />
        ))}
        {server !== null && server.form !== null && (
          <p className="fld-error" role="alert">
            {server.form}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
